/* Pufa global emoji-burst utility (window.PufaEmoji).
   ONE fixed-position particle engine shared by every "emoji explosion" effect:
   the dynamic picker (card select), the order wizard (per-tap thrill, the CTA
   idle emitter, the CTA click eruption) and anything added later.

   ── BEAT CHARTER (0.66.0 - every gamified beat in every consumer obeys this) ──
   1. State first: selection/price/DOM state paints BEFORE any beat fires; a
      beat may never delay or gate perceived feedback.
   2. Budget: <=6 particles per inline beat, <=10 per wizard stream (LOWEND <=6);
      one in-flight "feed" at a time (700ms debounce).
   3. Silence conditions: prefers-reduced-motion, document.hidden, and an ATC
      request already in flight all mean NO beat at all.
   4. Never celebrate errors, never before confirmation (cartError = silence;
      cartUpdate from 'product-form' = the one success signal).
   5. Honest numbers only: any celebrated count/price parses from DOM the
      shopper already sees, and FAILS CLOSED (render nothing) when unparsable.
   6. No ambient motion near the money button, no audio, no new asset weight.
   New beats that can't check every box don't ship.

   Particles live on <body> with position:fixed and are placed in VIEWPORT
   coordinates, so they can never grow the document or a scroll container -
   no scrollbar flicker, no layout shift. prefers-reduced-motion fully degrades.

   API:
     PufaEmoji.spawn(x, y, opts)   one particle at viewport (x, y)
     PufaEmoji.burst(rect, opts)   N particles from a DOMRect / {left,top,width,height}

   burst opts: count, glyphs[], spread ('center'|'surface'|'edge'),
               size, dist (number or () => number), rise, spin, life, dur. */
(function () {
  'use strict';
  if (typeof window === 'undefined' || window.PufaEmoji) return;

  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // Inject the particle CSS once - self-contained, no stylesheet dependency.
  var style = document.createElement('style');
  style.textContent =
    // a fixed, viewport-sized, clipped layer holds every particle -> they can never
    // extend the document or trigger a scrollbar, no matter where the origin is.
    '.pufa-emoji-layer{position:fixed;inset:0;overflow:hidden;pointer-events:none;z-index:2147483002}' +
    '.pufa-emoji{position:absolute;pointer-events:none;line-height:1;' +
    'transform:translate(-50%,-50%);opacity:0;font-size:22px;will-change:transform,opacity}' +
    '.pufa-emoji.go{animation:pufa-emoji-fly var(--pe-dur,1000ms) cubic-bezier(.18,.7,.35,1) forwards}' +
    '@keyframes pufa-emoji-fly{0%{opacity:0;transform:translate(-50%,-50%) scale(.4)}' +
    '18%{opacity:1}' +
    '100%{opacity:0;transform:translate(calc(-50% + var(--pe-dx,0px)),calc(-50% + var(--pe-dy,0px))) scale(1) rotate(var(--pe-r,0deg))}}' +
    // fill-chunk: a bead fragment that flies into the progress ball (flyTo {chunk:true})
    '.pufa-chunk{width:11px;height:11px;border-radius:50%;background:#c70d38;' +
    'box-shadow:0 0 0 2px rgba(255,255,255,.7),0 2px 6px rgba(199,13,56,.4)}' +
    '@media (prefers-reduced-motion:reduce){.pufa-emoji-layer{display:none!important}}';
  (document.head || document.documentElement).appendChild(style);

  var GLYPHS = ['🎁', '✨', '💎', '💫', '💖', '🥳', '🎉', '💝'];
  function val(v) { return typeof v === 'function' ? v() : v; }

  // Lazily-created clipping layer that all particles live in.
  var layer = null;
  function getLayer() {
    if (!layer || !layer.parentNode) {
      layer = document.createElement('div');
      layer.className = 'pufa-emoji-layer';
      layer.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(layer);
    }
    return layer;
  }

  // One particle at viewport coords (x, y).
  function spawn(x, y, o) {
    o = o || {};
    if (reduced) return null;
    var e = document.createElement('span');
    e.className = 'pufa-emoji';
    e.setAttribute('aria-hidden', 'true');
    e.textContent = o.glyph || GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
    e.style.left = x + 'px';
    e.style.top = y + 'px';
    if (o.size) e.style.fontSize = Math.round(o.size) + 'px';
    if (o.dur) e.style.setProperty('--pe-dur', o.dur + 'ms');
    var a = (o.angle != null) ? o.angle : Math.random() * Math.PI * 2;
    var d = (o.dist != null) ? o.dist : (28 + Math.random() * 40);
    e.style.setProperty('--pe-dx', Math.round(Math.cos(a) * d) + 'px');
    e.style.setProperty('--pe-dy', Math.round(Math.sin(a) * d - (o.rise || 0)) + 'px');
    e.style.setProperty('--pe-r', Math.round((Math.random() - 0.5) * (o.spin || 120)) + 'deg');
    getLayer().appendChild(e);
    void e.offsetWidth;            // reflow so the animation runs from the 0% state
    e.classList.add('go');
    var life = o.life || 1100;
    setTimeout(function () { if (e.parentNode) e.parentNode.removeChild(e); }, life);
    return e;
  }

  // N particles from a rect. spread: 'center' (default) | 'surface' | 'edge'.
  function burst(rect, o) {
    o = o || {};
    if (reduced || !rect) return;
    var n = o.count || 12;
    var glyphs = (o.glyphs && o.glyphs.length) ? o.glyphs : GLYPHS;
    var spread = o.spread || 'center';
    for (var i = 0; i < n; i++) {
      var ox, oy, angle = null;
      if (spread === 'surface') {
        ox = rect.left + Math.random() * rect.width;
        oy = rect.top + Math.random() * rect.height;
      } else if (spread === 'edge') {
        angle = Math.random() * Math.PI * 2;
        ox = rect.left + rect.width / 2 + Math.cos(angle) * rect.width / 2 * 0.94;
        oy = rect.top + rect.height / 2 + Math.sin(angle) * rect.height / 2 * 0.94;
      } else {
        ox = rect.left + rect.width / 2;
        oy = rect.top + rect.height / 2;
      }
      spawn(ox, oy, {
        glyph: glyphs[i % glyphs.length],
        size: val(o.size), dist: val(o.dist),
        angle: angle, rise: o.rise, spin: o.spin, life: o.life, dur: o.dur
      });
    }
  }

  // Resolve a flight endpoint to its current viewport center. Accepts a DOMRect,
  // an element, or a function returning either (re-called every frame). Returns
  // null for detached/zero-size targets so the caller keeps the last good point.
  function center(x) {
    if (!x) return null;
    if (typeof x === 'function') { try { x = x(); } catch (e) { return null; } }
    if (!x) return null;
    var r = x.getBoundingClientRect ? (x.isConnected === false ? null : x.getBoundingClientRect()) : x;
    if (!r || (!r.width && !r.height && !r.left && !r.top)) return null;
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width || 0, h: r.height || 0 };
  }

  // Fly ONE thing from one place to another ("feed the machine"): rises in an arc,
  // shrinks into the target. The target is RE-MEASURED so the flight lands
  // correctly even if layout shifts mid-air (the desktop wizard modal resizes
  // with its content). opts.chunk renders a crimson fill-chunk instead of a glyph.
  //
  // 0.61.0 (low-end perf): all concurrent flights share ONE rAF loop, and each
  // unique target is measured once per frame instead of once per flight. The
  // 15-ball capsule stream used to run 15 loops x 15 getBoundingClientRect per
  // frame - measured as the main jank source on throttled devices (29fps).
  var flights = [], pumping = false;
  function pump(now) {
    var mT = [], mP = []; // per-frame target measurement memo
    for (var i = flights.length - 1; i >= 0; i--) {
      var f = flights[i];
      if (f.done) { flights.splice(i, 1); continue; }
      var t = Math.min(1, (now - f.t0) / f.dur);
      var ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
      var mi = mT.indexOf(f.to), pt;
      if (mi >= 0) pt = mP[mi];
      else { pt = center(f.to); mT.push(f.to); mP.push(pt); }
      if (pt) f.p1 = pt; // live target tracking (memoized per frame)
      var x = f.p0.x + (f.p1.x - f.p0.x) * ease;
      var y = f.p0.y + (f.p1.y - f.p0.y) * ease - Math.sin(Math.PI * ease) * f.rise;
      if (f.ghost) {
        var sx = 1 + (((f.p1.w || f.p0.w) / Math.max(1, f.p0.w)) - 1) * ease;
        var sy = 1 + (((f.p1.h || f.p0.h) / Math.max(1, f.p0.h)) - 1) * ease;
        f.e.style.opacity = String(1 - 0.75 * ease * ease);
        f.e.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%) scale(' + sx + ',' + sy + ')';
      } else {
        var s = f.chunk ? (1 - 0.35 * ease) : (0.9 - 0.45 * ease + 0.6 * Math.sin(Math.PI * ease));
        f.e.style.transform = 'translate(' + x + 'px,' + y + 'px) translate(-50%,-50%) scale(' + s + ')' +
          (f.chunk ? '' : ' rotate(' + Math.round(24 * ease) + 'deg)');
      }
      if (t >= 1) { flights.splice(i, 1); f.finish(); }
    }
    if (flights.length) requestAnimationFrame(pump);
    else pumping = false;
  }
  function flyTo(from, to, o) {
    o = o || {};
    if (reduced) return;
    var p0 = center(from), p1 = center(to);
    if (!p0 || !p1) return;
    var e = document.createElement('span');
    e.className = 'pufa-emoji' + (o.chunk ? ' pufa-chunk' : '');
    e.setAttribute('aria-hidden', 'true');
    if (o.chunk && o.color) e.style.background = o.color; // e.g. gold bonus capsules
    if (o.chunk && o.size) { e.style.width = Math.round(o.size) + 'px'; e.style.height = Math.round(o.size) + 'px'; }
    if (o.ghost) {
      // ghost mode: a visual clone of the source control (same size/bg/radius) that
      // flies off and MORPHS to the target's dimensions - "the button merges into the bar"
      var gs = (from && from.getBoundingClientRect) ? getComputedStyle(from) : null;
      e.style.width = Math.round(p0.w) + 'px'; e.style.height = Math.round(p0.h) + 'px';
      e.style.background = o.background || (gs && gs.backgroundColor) || '#c70d38';
      e.style.borderRadius = (gs && gs.borderRadius) || '999px';
    } else if (!o.chunk) {
      e.textContent = o.glyph || '✨';
      e.style.fontSize = Math.round(o.size || 24) + 'px';
      e.style.filter = 'drop-shadow(0 2px 6px rgba(199,13,56,.35))';
    }
    e.style.left = '0px'; e.style.top = '0px'; // position purely via transform
    e.style.opacity = '1';
    getLayer().appendChild(e);
    var f = {
      e: e, to: to, p0: p0, p1: p1,
      dur: o.dur || (o.ghost ? 420 : 900),
      rise: (o.rise != null) ? o.rise : (o.ghost ? 0 : 70),
      t0: performance.now(), ghost: !!o.ghost, chunk: !!o.chunk, done: false,
      finish: function () {
        if (f.done) return; f.done = true;
        if (e.parentNode) e.parentNode.removeChild(e);
        if (typeof o.onArrive === 'function') { try { o.onArrive(); } catch (e2) {} }
      }
    };
    flights.push(f);
    if (!pumping) { pumping = true; requestAnimationFrame(pump); }
    setTimeout(f.finish, f.dur + 800); // backstop: hidden tab / stalled rAF still resolves
  }

  window.PufaEmoji = { spawn: spawn, burst: burst, flyTo: flyTo, GLYPHS: GLYPHS, reduced: reduced };
})();
